import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import { DS } from '../../theme';

interface TopNavProps {
  username?: string;
  onLogout: () => void;
  onMenuToggle?: () => void;
  isMobile?: boolean;
  isGuest?: boolean;
  onGuestSignIn?: () => void;
  onGuestSignUp?: () => void;
}

const TopNav: React.FC<TopNavProps> = ({
  username,
  onLogout,
  onMenuToggle,
  isMobile,
  isGuest,
  onGuestSignIn,
  onGuestSignUp,
}) => {
  const cleanName = username?.toLowerCase().startsWith('guest_') ? 'Guest' : username;
  const initials = cleanName ? cleanName.slice(0, 2).toUpperCase() : 'GU';

  return (
    <Box
      sx={{
        height: 56,
        minHeight: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, md: 3 },
        backgroundColor: DS.bg,
        borderBottom: `1px solid ${DS.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        flexShrink: 0,
      }}
    >
      {/* Left: hamburger on mobile */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isMobile && onMenuToggle && (
          <IconButton onClick={onMenuToggle} size="small" sx={{ color: DS.textMuted, mr: 0.5 }}>
            <MenuIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}

        {/* Guest mode butter bar — inline with right actions */}
        {isGuest && (
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: '8px',
              px: '12px',
              py: '6px',
              backgroundColor: DS.amberDim,
              border: `1px solid ${DS.amberBorder}`,
              borderRadius: '8px',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: DS.amber, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: DS.amber, whiteSpace: 'nowrap' }}>
              Guest mode — data won't be saved
            </Typography>
          </Box>
        )}
      </Box>

      {/* Right actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        {/* Guest: show Sign In + Sign Up buttons prominently */}
        {isGuest ? (
          <>
            <Button
              size="small"
              onClick={onGuestSignIn}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: DS.textMuted,
                px: 1.5,
                py: 0.625,
                borderRadius: '7px',
                '&:hover': { backgroundColor: DS.bg3, color: DS.text },
              }}
            >
              Sign In
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={onGuestSignUp}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                px: 1.75,
                py: 0.625,
                borderRadius: '7px',
                boxShadow: 'none',
              }}
            >
              Sign Up Free
            </Button>
          </>
        ) : (
          <>
            {/* Search — logged in users only */}
            <Tooltip title="Search">
              <Box
                sx={{
                  width: 34, height: 34, borderRadius: '8px',
                  border: `1px solid ${DS.border}`,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: DS.textMuted,
                  transition: 'all 0.15s ease',
                  '&:hover': { backgroundColor: DS.bg3, color: DS.text, borderColor: DS.borderStrong },
                }}
              >
                <SearchIcon sx={{ fontSize: 17 }} />
              </Box>
            </Tooltip>

            {/* Avatar */}
            <Tooltip title={cleanName || ''}>
              <Box
                sx={{
                  width: 34, height: 34, borderRadius: '8px',
                  backgroundColor: DS.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'default', flexShrink: 0,
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}>
                  {initials}
                </Typography>
              </Box>
            </Tooltip>
          </>
        )}

        {/* Sign out — always visible */}
        <Tooltip title={isGuest ? 'Sign out of guest mode' : 'Sign out'}>
          <IconButton
            size="small"
            onClick={onLogout}
            sx={{ color: DS.textMuted, ml: '2px', '&:hover': { color: DS.text, backgroundColor: DS.bg3 } }}
          >
            <LogoutIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default TopNav;
